import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { teacherAPI } from "../api";
import {
  EMPTY_REVIEW_COUNTS,
  REVIEW_POLL_INTERVAL_MS,
  getVisibleReviewItems,
  getVisibleReviewTotal,
  normalizeReviewCounts,
} from "../constants/reviewCounts";
import { getErrorMessage } from "../utils/apiError";
import { hasNotificationsPermission } from "../utils/permissions";
import { useAuth } from "./AuthContext";

const ReviewCountsContext = createContext(null);

export function ReviewCountsProvider({ children }) {
  const { user } = useAuth();
  const [counts, setCounts] = useState(EMPTY_REVIEW_COUNTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const inFlightRef = useRef(false);
  const canUseNotifications = hasNotificationsPermission(user);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (silent && inFlightRef.current) return;

    inFlightRef.current = true;

    if (!silent) {
      setLoading(true);
    }

    try {
      const data = await teacherAPI.getReviewCounts();
      setCounts(normalizeReviewCounts(data));
      setError("");
    } catch (err) {
      setError(getErrorMessage(err, "دریافت موارد نیازمند بررسی ناموفق بود"));
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();

    const intervalId = window.setInterval(() => {
      refresh({ silent: true });
    }, REVIEW_POLL_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refresh({ silent: true });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refresh]);

  const items = useMemo(
    () => getVisibleReviewItems(counts, { canUseNotifications }),
    [counts, canUseNotifications]
  );

  const total = useMemo(
    () => getVisibleReviewTotal(counts, { canUseNotifications }),
    [counts, canUseNotifications]
  );

  const pendingItems = useMemo(() => items.filter((item) => item.count > 0), [items]);

  const value = useMemo(
    () => ({
      counts,
      items,
      pendingItems,
      total,
      loading,
      error,
      refresh,
    }),
    [counts, items, pendingItems, total, loading, error, refresh]
  );

  return <ReviewCountsContext.Provider value={value}>{children}</ReviewCountsContext.Provider>;
}

export function useReviewCounts() {
  const context = useContext(ReviewCountsContext);

  if (!context) {
    throw new Error("useReviewCounts must be used within ReviewCountsProvider");
  }

  return context;
}
